/** @jsxImportSource @emotion/react */
import React, { PureComponent } from 'react';
import { jsx, css } from '@emotion/react';
import { FormattedDate, FormattedMessage, useIntl } from 'components/i18n';
import { useQuery } from 'react-query';
import api from 'libs/api';
import { colors } from '@atlaskit/theme';
import _ from 'lodash';
import { DateTime } from 'luxon';
import CreditcardIcon from '@atlaskit/icon/glyph/creditcard';
import EditIcon from '@atlaskit/icon/glyph/edit';
import EditorSuccessIcon from '@atlaskit/icon/glyph/editor/success';
import CheckCircleOutlineIcon from '@atlaskit/icon/glyph/check-circle-outline';
import AddCircleIcon from '@atlaskit/icon/glyph/add-circle';
import CheckIcon from '@atlaskit/icon/glyph/check';
import SendIcon from '@atlaskit/icon/glyph/send';
import AddIcon from '@atlaskit/icon/glyph/add';
import UndoIcon from '@atlaskit/icon/glyph/undo';
import ExportIcon from '@atlaskit/icon/glyph/export';
import CopyIcon from '@atlaskit/icon/glyph/copy';
import WatchIcon from '@atlaskit/icon/glyph/watch';

import { Link } from 'react-router-dom';
import { Payment, SaleEvent } from '../types';
import SendEmailPage from '../SendEmailPage';

type Props = {
  // saleId: string | null | undefined;
  events: SaleEvent[] | undefined;
  payments: Payment[] | undefined;
};

type EventProps = {
  event: SaleEvent;
  isLast: boolean;
};

type EventTemplateProps =  EventProps & {
  Icon?: React.ComponentType<any>;
  title: React.ReactNode;
  details?: React.ReactNode;
  hideTime?: boolean;
};

const EventTemplate = ({Icon = CheckCircleOutlineIcon, title, details, event, isLast, hideTime}: EventTemplateProps) => {
  return (
    <div css={css`display: flex;`}>
      <div css={css`margin-right: 1rem; display: flex; align-items: center; flex-direction: column;`}>
        <div css={css`margin-top: 0.2rem;`}>
          <Icon label={event.type} size="small" />
        </div>
        {!isLast && <div css={css`background-color: ${colors.N30}; width: 1px; flex: 1;`} />}
      </div>
      <div css={css`padding-bottom: 1.5rem;`}>
        {title}
        <div css={css`color: ${colors.subtleText()};`}>{details}</div>
        <div css={css`color: ${colors.subtleText()};`}><FormattedDate value={event.timestamp} hour={hideTime? undefined: 'numeric'} minute={hideTime? undefined: 'numeric'} /></div>
        <div css={css`color: ${colors.subtleText()};`}>{event.user?.name}</div>
      </div>
    </div>
  );
};

const EmailSentEvent = (props: EventProps) => {
  const {event} = props;
  const title = (
    <FormattedMessage
      id='sale.SaleEvents.EmailSentEvent.title'
      defaultMessage='Email sent to {to}'
      values={event.data}
    />
  );
  return <EventTemplate {...props} title={title} Icon={SendIcon} />;
};

const EmailOpenedEvent = (props: EventProps) => {
  const {event} = props;
  const title = (
    <FormattedMessage
      id='sale.SaleEvents.EmailOpenedEvent.title'
      defaultMessage='Email opened by {recipient}'
      values={event.data}
    />
  );
  return <EventTemplate {...props} title={title} Icon={WatchIcon} />;
};

const MarkedSentEvent = (props: EventProps) => {
  const {event} = props;
  const title = (
    <FormattedMessage
      id='sale.SaleEvents.MarkedSentEvent.title'
      defaultMessage='Marked as sent'
      values={event.data}
    />
  );
  return <EventTemplate {...props} title={title} Icon={SendIcon} />;
};

const CreatedEvent = (props: EventProps) => {
  const {event} = props;
  const title = (
    <FormattedMessage
      id='sale.SaleEvents.CreatedEvent.title'
      defaultMessage='Invoice created'
      values={event.data}
    />
  );
  let details = null;
  if (event.data.recurringSaleId) {
    details = (
      <div>
        <FormattedMessage
          id='sale.SaleEvents.CreatedEvent.basedOn'
          defaultMessage='Based on a'
        />
        {' '}
        <Link to={`../recurring-invoices/${event.data.recurringSaleId}`}>
          <FormattedMessage
            id='sale.SaleEvents.CreatedEvent.recurringInvoice'
            defaultMessage='recurring invoice'
          />
        </Link>
      </div>
    );
  }
  return <EventTemplate {...props} title={title} details={details} Icon={CheckCircleOutlineIcon} />;
};

const UpdatedEvent = (props: EventProps) => {
  const {event} = props;
  const title = (
    <FormattedMessage
      id='sale.SaleEvents.UpdatedEvent.title'
      defaultMessage='Invoice updated'
      values={event.data}
    />
  );
  return <EventTemplate {...props} title={title} Icon={EditIcon} />;
};

const DuplicatedEvent = (props: EventProps) => {
  const {event} = props;
  const title = (
    <div>
      <FormattedMessage
        id='sale.SaleEvents.DuplicatedEvent.title'
        defaultMessage='Invoice duplicated'
        values={event.data}
      />
      {' ('}
      <Link to={`../invoices/${event.data.id}`}>
        #
        {event.data.invoiceNumber}
      </Link>
      )
    </div>
  );
  return <EventTemplate {...props} title={title} Icon={CopyIcon} />;
};

const CreditedEvent = (props: EventProps) => {
  const {event} = props; // TODO add hyperlink to new invoice
  const title = (
    <div>
      <FormattedMessage
        id='sale.SaleEvents.CreditedEvent.title'
        defaultMessage='Invoice credited'
        values={event.data}
      />
      {' ('}
      <Link to={`../invoices/${event.data.id}`}>
        #
        {event.data.invoiceNumber}
      </Link>
      )
    </div>
  );
  return <EventTemplate {...props} title={title} Icon={UndoIcon} />;
};

const PaymentReceivedEvent = (props: EventProps) => {
  const intl = useIntl();
  const {event} = props;
  const title = (
    <div>
      <FormattedMessage
        id='sale.SaleEvents.ReceivedPaymentEvent.title'
        defaultMessage='Received payment of {amount}'
        values={{amount: intl.formatNumber(event.data.amount, {style: 'currency', currency: 'EUR'})}}
      />
    </div>
  );
  const MoneyIcon = (iconProps: any) => {
    return <span {...iconProps}>€</span>;
  };
  return <EventTemplate {...props} title={title} Icon={MoneyIcon} hideTime />;
};

const Event = (props: EventProps) => {
  const {event} = props;
  const eventComponents = {
    'sale.created': CreatedEvent,
    'sale.updated': UpdatedEvent,
    'sale.email_sent': EmailSentEvent,
    'sale.email_opened': EmailOpenedEvent,
    'sale.marked_sent': MarkedSentEvent,
    'sale.duplicated': DuplicatedEvent,
    'sale.credited': CreditedEvent,
    'sale.payment_received': PaymentReceivedEvent,
  };
  const EventComponent = eventComponents[event.type];
  if (!EventComponent) return <span />;
  return <EventComponent {...props} />;
};

const SaleTimeline: React.FC<Props> = ({ events: regularEvents, payments}) => {

  let events = regularEvents || [];

  if (payments) {
    events = events.concat(payments.map(payment => {
      const paymentEvent: SaleEvent = {
        id: payment.id,
        type: 'sale.payment_received',
        timestamp: DateTime.fromISO(payment.date).endOf('day').toISO(), // to show on top (as the last event of the day)
        data: payment
      };
      return paymentEvent;
    }));
  }

  return (
    <div css={css`margin-top: 2rem;`}>
      {_.orderBy(events, ['timestamp'], ['desc']).map((event: SaleEvent, index) => (
        <Event event={event} key={event.id} isLast={index === events.length-1} />
      ))}

    </div>
  );
};

export default SaleTimeline;
